import { getCompanyUsers } from 'lib/api';
import { getAllRoles } from 'lib/api/users/roles';
import PortfolioTransfer from 'lib/common/PortfolioTransfer';
import {
  CountryOptionKey,
  MaritalStatusOptionKey,
  translateCountry,
  translateMaritalStatus,
} from 'lib/consts/contactFields';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import EnhancedTable from 'lib/designSystem/Table';
import { validatePermissions } from 'lib/functions/utils';
import { Role } from 'lib/types/models';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { AuthState } from 'services/redux/authSlice';

import CollectionPortfolioDialog from './components/CollectionPortfolio';
import CreateUserDialog from './components/CreateUser';
import DeactivateUser from './components/DeactivateUser';
import DeleteUser from './components/DeleteUser';
import EditUserDialog from './components/EditUser';
import EditUserBusinesses from './components/EditUserBusinesses';
import EnrollmentLinkDialog from './components/EnrollmentLink';
import ResendUserEmailDialog from './components/ResendUserEmail';
import Subordinate from './components/Subordinate';

export interface UserInterface {
  id?: number;
  name: string;
  email?: string;
  permissionPolicy: string;
  password?: string;
  roles?: string[];
  rolesNames?: string[];
  active?: boolean;
  permissionPolicyCode?: number;
  subortinatedUsers?: UserInterface[];
  identifier?: string;
  phone?: string;
  country?: CountryOptionKey;
  profession?: string;
  maritalStatus?: MaritalStatusOptionKey;
  isSigner?: boolean;
}

export interface CompanyRelations {
  id?: number;
  businessName: string;
  businessId: number;
}

export const nullUserState = {
  id: 0,
  name: '',
  email: '',
  permissionPolicy: '',
  rolesNames: [],
  password: '',
  subordinatedUsers: [],
  identifier: '',
  phone: '',
  isSigner: false,
};

const UsersLists: React.FC = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState<UserInterface[]>([]);
  const [companyRelations, setCompanyRelations] = useState<CompanyRelations[]>(
    [],
  );
  const [openEnrollmentLinkDialog, setOpenEnrollmentLinkDialog] =
    useState(false);
  const [loading, setLoading] = useState(true);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openResendDialog, setOpenResendDialog] = useState(false);
  const [openDeactivateDialog, setOpenDeactivateDialog] = useState(false);
  const [editType, setEditType] = useState<'password' | 'info'>('info');

  const [openCompanyDialog, setOpenCompanyDialog] = useState(false);
  const [openCollectionPortfolioDialog, setOpenCollectionPortfolioDialog] =
    useState(false);

  const [selectedUser, setSelectedUser] =
    useState<UserInterface>(nullUserState);
  const [roles, setRoles] = useState<Role[]>([]);
  const loggedUser = useSelector(
    (state: { auth: AuthState }) => state.auth.user,
  );
  const [openPortfolioTransfer, setOpenPortfolioTransfer] = useState(
    searchParams.has('opentransfer') ? true : false,
  );
  const [reload, setReload] = useState(0);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [userToTransfer, setUserToTransfer] = useState<number | undefined>();
  const [openSubordinates, setOpenSubordinates] = useState(false);

  const handleOpenAddDialog = async () => {
    setLoading(true);
    const rolesData = await getAllRoles(dispatch);
    setRoles(rolesData);
    setSelectedUser(nullUserState);
    setOpenAddDialog(true);
    setLoading(false);
  };
  const handleOpenEditDialog = async (id: number) => {
    const user = users.find((x: UserInterface) => x.id === id);
    if (!user) return alert('Ha ocurrido un errorf');

    setLoading(true);
    const rolesData = await getAllRoles(dispatch);
    setRoles(rolesData);
    setEditType('info');
    setSelectedUser(user);
    setOpenEditDialog(true);
    setLoading(false);
  };

  const handleOpenEditPasswordDialog = (id: number) => {
    const user = users.find((x: UserInterface) => x.id === id);
    if (!user) return alert('Ha ocurrido un errore');

    user.password = '';

    setEditType('password');
    setSelectedUser(user);
    setOpenEditDialog(true);
  };

  const handleResendEditDialog = (id: number) => {
    const user = users.find((x: UserInterface) => x.id === id);
    if (!user) return alert('Ha ocurrido un errora');

    setSelectedUser(user);
    setOpenResendDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenAddDialog(false);
    setSelectedUser(nullUserState);
    setOpenEditDialog(false);
    setOpenDeactivateDialog(false);
    setOpenCompanyDialog(false);
    setOpenCollectionPortfolioDialog(false);
    setOpenResendDialog(false);
    setOpenDeleteDialog(false);
    setOpenSubordinates(false);
    setOpenEnrollmentLinkDialog(false);
  };

  const handleOpenEnrollmentLinkDialog = (id: number) => {
    const user = users.find((x: UserInterface) => x.id === id);
    if (!user) return alert('Ha ocurrido un error');

    setSelectedUser(user);
    setOpenEnrollmentLinkDialog(true);
  };

  const handleOpenCompaniesDialog = async (id: number) => {
    setLoading(true);
    const user = users.find((x: UserInterface) => x.id === id);
    if (!user) {
      setLoading(false);
      return alert('Ha ocurrido un error-');
    }
    setSelectedUser(user);
    setOpenCompanyDialog(true);
    setLoading(false);
  };

  const handleOpenCollectionPortfolioDialog = async (id: number) => {
    setLoading(true);
    const user = users.find((x: UserInterface) => x.id === id);
    if (!user) {
      setLoading(false);
      return alert('Ha ocurrido un error');
    }
    setSelectedUser(user);
    setOpenCollectionPortfolioDialog(true);
    setLoading(false);
  };

  const handleDeactivateDialog = async (id: number) => {
    setLoading(true);
    const user = users.find((x: UserInterface) => x.id === id);
    if (!user) {
      setLoading(false);
      return alert('Ha ocurrido un error');
    }
    setSelectedUser(user);
    setOpenDeactivateDialog(true);
    setLoading(false);
  };

  const handleDeleteDialog = async (id: number) => {
    setLoading(true);
    const user = users.find((x: UserInterface) => x.id === id);
    if (!user) {
      setLoading(false);
      return alert('Ha ocurrido un error');
    }
    setSelectedUser(user);
    setOpenDeleteDialog(true);
    setLoading(false);
  };

  const handleOpenSubordinatesDialog = async (id: number) => {
    setLoading(true);
    const user = users.find((x: UserInterface) => x.id === id);
    if (!user) {
      setLoading(false);
      return alert('Ha ocurrido un error');
    }
    setSelectedUser(user);
    setOpenSubordinates(true);
    setLoading(false);
  };

  const fetchUsers = async () => {
    const users = await getCompanyUsers(dispatch);
    const response = users.map((user: UserInterface) => ({
      ...user,
      translatedCountry: translateCountry(user.country),
      translatedProfession: user.profession,
      translatedMaritalStatus: translateMaritalStatus(user.maritalStatus),
    }));
    setUsers(response || []);
  };

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        await fetchUsers();
      } finally {
        setLoading(false);
      }
    })();
  }, [reload]);

  if (loading) return <CenteredCircularProgress />;

  const canShowEditDialog = (id: number) => {
    const user = users.find((x: UserInterface) => x.id === id);
    if (!user) return alert('Ha ocurrido un error');

    if (user.id === loggedUser?.id) return true;

    return validatePermissions(loggedUser, 'users_create_new');
  };

  const canShowResendEmail = (id: number) => {
    const user = users.find((x: UserInterface) => x.id === id);
    if (!user) return alert('Ha ocurrido un erro');

    return !user.active;
  };

  const canShowDeactivateDialog = (id: number) => {
    const user = users.find((x: UserInterface) => x.id === id);
    if (!user) return alert('Ha ocurrido un error');

    if (!user.active) return false;

    return validatePermissions(loggedUser, 'users_create_new');
  };

  const canShowClientDialog = () => {
    return validatePermissions(loggedUser, 'users_create_business_association');
  };

  const canShowCollectionPortfolioDialog = (id: number) => {
    const user = users.find((x: UserInterface) => x.id === id);
    if (!user) return alert('Ha ocurrido un error');

    return (
      validatePermissions(user, 'have_collection_portfolio') &&
      validatePermissions(loggedUser, 'edit_collection_portfolio')
    );
  };

  const canShowSubordinatesDialog = (id: number) => {
    const user = users.find((x: UserInterface) => x.id === id);
    if (!user) return alert('Ha ocurrido un error');

    return (
      (validatePermissions(loggedUser, 'users_create_new') ||
        validatePermissions(loggedUser, 'edit_subordinates')) &&
      validatePermissions(user, 'have_subordinates')
    );
  };

  const actions = [
    {
      action: handleOpenEnrollmentLinkDialog,
      actionLabel: 'Link de enrolamiento',
    },
    {
      action: handleOpenCompaniesDialog,
      actionLabel: 'Ver Clientes',
      canShow: canShowClientDialog,
    },
    {
      action: handleOpenCollectionPortfolioDialog,
      actionLabel: 'Ver cartera cobranza',
      canShow: canShowCollectionPortfolioDialog,
    },

    {
      action: handleOpenSubordinatesDialog,
      actionLabel: 'Editar subordinados',
      canShow: canShowSubordinatesDialog,
    },
    {
      action: handleOpenEditDialog,
      actionLabel: 'Editar roles',
      canShow: canShowEditDialog,
    },
    {
      action: handleOpenEditPasswordDialog,
      actionLabel: 'Editar información',
      canShow: canShowEditDialog,
    },
    {
      action: handleResendEditDialog,
      actionLabel: 'Reactivar',
      canShow: canShowResendEmail,
    },
    {
      action: handleDeactivateDialog,
      actionLabel: 'Desactivar usuario',
      canShow: canShowDeactivateDialog,
    },
    {
      action: handleDeleteDialog,
      actionLabel: 'Eliminar usuario',
      canShow: canShowEditDialog,
    },
  ];

  const startButtons: CustomButtonProps[] = [
    {
      action: () => setOpenPortfolioTransfer(true),
      name: 'Transferir cartera',
      color: 'secondary',
    },
    {
      action: () => void handleOpenAddDialog(),
      name: 'Crear usuario',
      color: 'primary',
      disabled: !validatePermissions(loggedUser, 'users_create_new'),
    },
  ];

  const dataHeaders = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Nombre' },
    { key: 'email', label: 'Email', type: 'copyable' },
    { key: 'active', label: 'Activo', type: 'boolean-active' },
    { key: 'rolesNamesShow', label: 'Nivel de permisos' },
  ];

  return (
    <div>
      <HeaderWithActions
        title="Aquí encontrarás todos los usuarios de tu empresa."
        variant="body1"
        buttons={startButtons}
        type="subHeader"
      />
      <EnhancedTable
        dataHeaders={dataHeaders}
        data={users}
        actions={actions}
        showActions
        rowsPerPageDefault={10}
        lastElement
      />
      <EditUserDialog
        loading={loading}
        setLoading={setLoading}
        handleCloseDialog={handleCloseDialog}
        openEditDialog={openEditDialog}
        fetchUsers={fetchUsers}
        editType={editType}
        roles={roles}
        setRoles={setRoles}
        selectedUser={selectedUser}
        setSelectedUser={setSelectedUser}
      />
      {openCompanyDialog && (
        <EditUserBusinesses
          handleCloseDialog={handleCloseDialog}
          openCompanyDialog={openCompanyDialog}
          selectedUser={selectedUser}
          setCompanyRelations={setCompanyRelations}
          companyRelations={companyRelations}
        />
      )}
      {openCollectionPortfolioDialog && (
        <CollectionPortfolioDialog
          handleCloseDialog={handleCloseDialog}
          openCollectionPortfolioDialog={openCollectionPortfolioDialog}
          selectedUser={selectedUser}
        />
      )}
      <CreateUserDialog
        loading={loading}
        setLoading={setLoading}
        handleCloseDialog={handleCloseDialog}
        openAddDialog={openAddDialog}
        roles={roles}
        setRoles={setRoles}
        fetchUsers={fetchUsers}
      />
      {openEnrollmentLinkDialog && (
        <EnrollmentLinkDialog
          user={selectedUser}
          handleCloseDialog={handleCloseDialog}
          openAddDialog={openEnrollmentLinkDialog}
          setLoading={setLoading}
        />
      )}
      <ResendUserEmailDialog
        loading={loading}
        setLoading={setLoading}
        selectedUser={selectedUser}
        handleCloseDialog={handleCloseDialog}
        openAddDialog={openResendDialog}
      />
      {openPortfolioTransfer && (
        <PortfolioTransfer
          open={openPortfolioTransfer}
          setOpen={setOpenPortfolioTransfer}
          userToTransfer={userToTransfer}
        />
      )}

      <DeactivateUser
        open={openDeactivateDialog}
        userId={selectedUser.id as number}
        userEmail={selectedUser.email as string}
        handleClose={handleCloseDialog}
        reload={reload}
        setReload={setReload}
        setUserToTransfer={setUserToTransfer}
        setOpenPortfolioTransfer={setOpenPortfolioTransfer}
      />
      <DeleteUser
        open={openDeleteDialog}
        userId={selectedUser.id as number}
        userEmail={selectedUser.email as string}
        handleClose={handleCloseDialog}
        reload={reload}
        setReload={setReload}
        setUserToTransfer={setUserToTransfer}
        setOpenPortfolioTransfer={setOpenPortfolioTransfer}
      />
      <Subordinate
        open={openSubordinates}
        setClose={() => handleCloseDialog()}
        user={selectedUser}
        users={users}
      />
    </div>
  );
};

export default UsersLists;
